/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getClientOrderSettings } from 'common/services/OrderService';
import {
    DOCUMENT_TRADE_SUB_TYPE,
    DOCUMENT_TRADE_TYPE,
    ORDER_FULFILLMENT_TYPE,
    WORK_ORDER_PRIORITY_TYPE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import {
    Options,
    Property,
    State,
} from 'admin/stores/redux/AdminOrderCreation/interfaces';
import { UpdateFulfillmentDetailsPayload } from 'common/types/redux/orders';
import { GenericRecord, Order, DocumentContact } from 'common/types';
import { UpdateKeyValuePairPayload } from 'common/stores/redux/interfaces';
import { initializeOrder } from 'admin/stores/redux/AdminOrder/helpers';
import { initContact } from 'admin/utils/contact-utils/contact-utils';
import {
    ContactIdentity,
    createContactDraftReducers,
    isSameContact,
} from 'admin/stores/redux/contactSliceHelpers/contactSliceHelpers';

const newDraftContact = () => initContact();

// Stable identity: a fresh [] here re-renders every subscriber on
// every dispatch.
const EMPTY_CONTACTS: DocumentContact[] = [];

const initialState: State = {
    order: null,
    initialized: false,
    draftContact: newDraftContact(),
};

// Async thunk for updating client
export const updateClient = createAsyncThunk(
    'orderCreation/updateClient',
    async ({
        clientId,
        currentClientId,
    }: {
        clientId: number;
        currentClientId: number;
    }) => {
        if (clientId !== currentClientId) {
            const {
                hasFulfillmentMethods,
                propertyRequired,
                enableDisplayAvailableStockOption,
                enableDisplayAHRIFilter,
            } = await getClientOrderSettings(clientId);

            return {
                clientId,
                hasFulfillmentMethods,
                propertyRequired,
                enableDisplayAvailableStockOption,
                enableDisplayAHRIFilter,
            };
        }

        return null;
    }
);

const adminOrderCreationSlice = createSlice({
    name: 'adminOrderCreation',
    initialState,
    reducers: {
        initialize: (state: State, action: PayloadAction<Options>) => {
            const options = action.payload;

            // If order already exists with same uuid, don't reinitialize
            if (state.order && state.order.uuid === options.uuid) {
                return;
            }

            state.initialized = true;
            state.order = initializeOrder(options) as Order;
        },

        reset: (state: State) => {
            state.order = null;
            state.initialized = false;
            state.draftContact = newDraftContact();
        },

        updateKeyValuePair: (
            state: State,
            action: PayloadAction<UpdateKeyValuePairPayload>
        ) => {
            if (!state.order) return;

            const { key, value } = action.payload;
            const update: GenericRecord<unknown> = {};

            if (Array.isArray(key)) {
                for (let idx = 0; idx < key.length; idx++) {
                    update[key[idx]] = value[idx];
                }
            } else {
                update[key] = value;
            }

            state.order = { ...state.order, ...update };
        },

        updateSpecialInstructions: (
            state: State,
            action: PayloadAction<string>
        ) => {
            if (!state.order) {
                return;
            }

            state.order = {
                ...state.order,
                instructions: {
                    special: action.payload,
                },
            };
        },

        updateProperty: (state, action: PayloadAction<Property>) => {
            if (!state.order) {
                return;
            }

            const property = action.payload;
            state.order.propertyDisplay = property;
            state.order.propertyId = property?.value?.id ?? null;
        },

        addContact: (state: State, action: PayloadAction<DocumentContact>) => {
            if (!state.order) {
                return;
            }

            state.order.contacts = [
                ...(state.order.contacts ?? []),
                action.payload,
            ];
        },

        removeContact: (
            state: State,
            action: PayloadAction<ContactIdentity>
        ) => {
            if (!state.order) {
                return;
            }

            state.order.contacts = state.order.contacts?.filter(
                contact => !isSameContact(contact, action.payload)
            );
        },

        ...createContactDraftReducers<State>(newDraftContact),

        updateFulfillmentDetails: (
            state,
            action: PayloadAction<UpdateFulfillmentDetailsPayload>
        ) => {
            const { fulfillmentDetails, fulfillmentDistributionCenter } =
                action.payload;

            if (
                !state.order ||
                (!fulfillmentDetails && !fulfillmentDistributionCenter)
            ) {
                return;
            }

            const {
                fulfillmentTypeId,
                fulfillmentDistributionCenterId,
                distributionCenterId,
                fulfillmentToAddress = '',
                fulfillmentToCity = '',
                fulfillmentToRegion = '',
                fulfillmentToPostalCode = '',
                fulfillmentTo = '',
                residentialDelivery = false,
            } = fulfillmentDetails;

            state.order = {
                ...state.order,
                fulfillmentTypeId: fulfillmentTypeId as ORDER_FULFILLMENT_TYPE,
                fulfillmentDistributionCenterId:
                    fulfillmentDistributionCenterId ||
                    distributionCenterId ||
                    0,
                fulfillmentToAddress,
                fulfillmentToCity,
                fulfillmentToRegion,
                fulfillmentToPostalCode,
                fulfillmentTo,
                residentialDelivery,
                vendorDistributionCenter: fulfillmentDistributionCenter,
            };
        },

        updateTradeType: (
            state: State,
            action: PayloadAction<DOCUMENT_TRADE_TYPE>
        ) => {
            if (!state.order) {
                return;
            }

            const tradeTypeId = action.payload;
            const update: Partial<Order> = {
                tradeTypeId,
                tradeSubTypeId: state.order.tradeSubTypeId,
            };

            // If trade type is not plumbing and current trade sub type is waterheater, reset to residential
            if (
                tradeTypeId !== DOCUMENT_TRADE_TYPE.PLUMBING &&
                state.order.tradeSubTypeId ===
                    DOCUMENT_TRADE_SUB_TYPE.WATERHEATER
            ) {
                update.tradeSubTypeId = DOCUMENT_TRADE_SUB_TYPE.RESIDENTIAL;
            }

            state.order = { ...state.order, ...update };
        },

        setDefaultTradeAndPriority: (state: State) => {
            if (!state.order) {
                return;
            }

            state.order.tradeTypeId = DOCUMENT_TRADE_TYPE.HVAC;
            state.order.tradeSubTypeId = DOCUMENT_TRADE_SUB_TYPE.RESIDENTIAL;
            state.order.priorityId = WORK_ORDER_PRIORITY_TYPE.ROUTINE;
        },
    },
    extraReducers: builder => {
        builder.addCase(updateClient.fulfilled, (state: State, action) => {
            if (!state.order || !action.payload) {
                return;
            }

            const {
                clientId,
                hasFulfillmentMethods,
                propertyRequired,
                enableDisplayAvailableStockOption,
                enableDisplayAHRIFilter,
            } = action.payload;

            const fulfillmentMethod = hasFulfillmentMethods
                ? state.order.fulfillmentMethod
                : null;

            state.order = {
                ...state.order,
                clientId,
                hasFulfillmentMethods,
                fulfillmentMethod,
                propertyRequired,
                propertyDisplay: {
                    label: '',
                    value: {
                        id: 0,
                        suggestion: '',
                    },
                },
                propertyId: undefined,
                enableDisplayAvailableStockOption,
                enableDisplayAHRIFilter,
            };
        });
    },
});

export const {
    initialize,
    reset,
    updateKeyValuePair,
    updateSpecialInstructions,
    updateProperty,
    addContact,
    removeContact,
    setDraftContact,
    resetDraftContact,
    updateFulfillmentDetails,
    updateTradeType,
    setDefaultTradeAndPriority,
} = adminOrderCreationSlice.actions;

interface StateSlice {
    adminOrderCreation: State;
}

export const orderCreationSelectedContacts = (state: StateSlice) =>
    state.adminOrderCreation.order?.contacts ?? EMPTY_CONTACTS;

export const orderCreationDraftContact = (state: StateSlice) =>
    state.adminOrderCreation.draftContact;

export default adminOrderCreationSlice.reducer;
